import * as React from 'react'
import MdEditor from 'react-markdown-editor-lite'

import { cn } from '@/lib/utils'
import { useMarkdownEditorPageScroll } from '@/hooks/use-markdown-editor-page-scroll'
import { useMarkdownEditorSplitResize } from '@/hooks/use-markdown-editor-split-resize'
import { toast } from '@/hooks/use-toast'

import MarkdownAudioPhraseDialog from '@/components/markdown/markdown-audio-phrase-dialog'
import MarkdownAudioPhraseToolbarPlugin from '@/components/markdown/markdown-audio-phrase-toolbar-plugin'
import MarkdownPreview from '@/components/markdown/markdown-preview'
import MarkdownUnderlineToolbarPlugin from '@/components/markdown/markdown-underline-toolbar-plugin'

import uploadService from '@/services/upload.service'
import { MarkdownAudioAttachmentType } from '@/utils/audio-phrase-markdown'
import { ErrorResponse } from '@/utils/axios-error-handle'
import { getMarkdownEditorSelection, insertMarkdownAudio } from '@/utils/markdown-editor-selection'

const audioPhraseDialogHandlerRef: { current: (() => void) | null } = { current: null }
let audioPhrasePluginRegistered = false
let underlinePluginRegistered = false

function ensureAudioPhraseToolbarPlugin() {
  if (audioPhrasePluginRegistered) return
  MdEditor.use(MarkdownAudioPhraseToolbarPlugin, {
    onAttach: () => audioPhraseDialogHandlerRef.current?.(),
  })
  audioPhrasePluginRegistered = true
}

function ensureUnderlineToolbarPlugin() {
  if (underlinePluginRegistered) return
  // Same pluginName as the built-in — latest registration wins
  MdEditor.use(MarkdownUnderlineToolbarPlugin)
  underlinePluginRegistered = true
}

ensureUnderlineToolbarPlugin()

function renderHTML(text: string) {
  return (
    <div className="md-editor-preview-content">
      <MarkdownPreview>{text}</MarkdownPreview>
      <div className="md-editor-preview-end" aria-hidden="true" />
    </div>
  )
}

interface ReactMarkdownEditorLiteProps {
  onChangeContent?: (v?: string) => void
  id: string
  value?: string
  className?: string
  enableAudioPhrases?: boolean
  /** Use inside cards/collapsibles — disables viewport-sticky toolbar and full-page height */
  embedded?: boolean
  view?: {
    html: boolean
    md: boolean
    menu: boolean
  }
  style?: React.CSSProperties
}

function MarkdownEditor({
  id,
  className,
  onChangeContent,
  value: initialValue,
  enableAudioPhrases = false,
  embedded = false,
  view,
  style,
}: ReactMarkdownEditorLiteProps) {
  const editorRef = React.useRef<HTMLDivElement>(null)
  const [defaultValue, setDefaultValue] = React.useState<string>(initialValue || '')
  const [audioDialogOpen, setAudioDialogOpen] = React.useState(false)
  const [selectedText, setSelectedText] = React.useState('')
  const [selectionRange, setSelectionRange] = React.useState<{ start: number; end: number } | null>(null)
  useMarkdownEditorSplitResize(editorRef)
  useMarkdownEditorPageScroll(editorRef, embedded)

  // Raise <main> stacking while maximized so fixed fullscreen sits above sticky header/sidebar
  // without moving DOM nodes out of the React tree (which breaks toolbar click handlers).
  React.useEffect(() => {
    const root = editorRef.current
    if (!root) return

    const editorEl = root.querySelector('.rc-md-editor') as HTMLElement | null
    if (!editorEl) return

    const syncFullscreenClass = () => {
      document.body.classList.toggle('md-editor-is-fullscreen', editorEl.classList.contains('full'))
    }

    const observer = new MutationObserver(syncFullscreenClass)
    observer.observe(editorEl, { attributes: true, attributeFilter: ['class'] })
    syncFullscreenClass()

    return () => {
      observer.disconnect()
      document.body.classList.remove('md-editor-is-fullscreen')
    }
  }, [])

  const handleEditorChange = React.useCallback(
    ({ text }: { html?: string; text?: string }) => {
      onChangeContent?.(text || '')
      setDefaultValue(text || '')
    },
    [onChangeContent]
  )

  const onImageUpload = React.useCallback(async (file: File) => {
    try {
      const data = await uploadService.uploadImageComment({ image: file })
      return data.url
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error)
      const _error = error as ErrorResponse
      toast({
        title: 'Error',
        description: _error.response?.data.message || 'Failed to upload image',
        variant: 'destructive',
      })
      return ''
    }
  }, [])

  const openAudioPhraseDialog = React.useCallback(() => {
    const selection = getMarkdownEditorSelection(editorRef.current)
    if (!selection) return

    setSelectedText(selection.selectedText)
    setSelectionRange({ start: selection.start, end: selection.end })
    setAudioDialogOpen(true)
  }, [])

  audioPhraseDialogHandlerRef.current = openAudioPhraseDialog

  if (enableAudioPhrases) {
    ensureAudioPhraseToolbarPlugin()
  }

  React.useEffect(() => {
    if (!enableAudioPhrases) return

    const container = editorRef.current
    if (!container) return

    const handleBoldTrigger = (event: Event) => {
      event.preventDefault()
      event.stopPropagation()
      openAudioPhraseDialog()
    }

    const handleToolbarClick = (event: MouseEvent) => {
      const boldButton = (event.target as HTMLElement).closest('.button-type-bold')
      if (!boldButton || !container.contains(boldButton)) return
      handleBoldTrigger(event)
    }

    const handleKeyboardShortcut = (event: KeyboardEvent) => {
      const textarea = container.querySelector('textarea')
      if (event.target !== textarea) return
      if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'b') return
      handleBoldTrigger(event)
    }

    container.addEventListener('click', handleToolbarClick, true)
    container.addEventListener('keydown', handleKeyboardShortcut, true)

    const setBoldTitle = () => {
      container.querySelector('.button-type-bold')?.setAttribute('title', 'Attach audio')
    }
    requestAnimationFrame(setBoldTitle)

    return () => {
      container.removeEventListener('click', handleToolbarClick, true)
      container.removeEventListener('keydown', handleKeyboardShortcut, true)
      container.querySelector('.button-type-bold')?.setAttribute('title', 'Bold')
    }
  }, [enableAudioPhrases, openAudioPhraseDialog])

  const handleAudioInsert = React.useCallback(
    ({ type, snippet }: { type: MarkdownAudioAttachmentType; snippet: string }) => {
      if (!selectionRange) return
      const nextValue = insertMarkdownAudio({
        container: editorRef.current,
        currentValue: defaultValue,
        snippet,
        selection: {
          ...selectionRange,
          selectedText: selectedText,
        },
        mode: type,
      })
      setDefaultValue(nextValue)
      onChangeContent?.(nextValue)
    },
    [defaultValue, onChangeContent, selectedText, selectionRange]
  )

  const editorStyle = embedded
    ? { height: '360px', minHeight: '320px', maxHeight: '480px', ...style }
    : { height: '70vh', minHeight: '480px', maxHeight: '960px', ...style }

  return (
    <div ref={editorRef} className={cn('space-y-2', embedded && 'markdown-editor-embedded')}>
      <MdEditor
        style={editorStyle}
        syncScrollMode={[]}
        renderHTML={renderHTML}
        onImageUpload={onImageUpload}
        onChange={handleEditorChange}
        className={cn(embedded ? 'mb-0' : 'mb-6', className)}
        id={id}
        view={view}
        value={defaultValue || ''}
        config={{
          // Flush toolbar formatting into undo history immediately so Ctrl/Cmd+Z undoes bold/italic as a unit
          loggerInterval: 0,
          // Disable the expand-less/more chevron that toggles the toolbar/header
          canView: {
            menu: true,
            md: true,
            html: true,
            both: true,
            fullScreen: true,
            hideMenu: false,
          },
        }}
        plugins={[
          'header',
          'font-bold',
          'font-italic',
          'font-underline',
          'font-strikethrough',
          'list-unordered',
          'list-ordered',
          'block-quote',
          'block-wrap',
          'block-code-inline',
          'block-code-block',
          'table',
          'image',
          'link',
          'clear',
          'logger',
          ...(enableAudioPhrases ? ['audio-phrase-attach' as const] : []),
          'mode-toggle',
          'full-screen',
          'tab-insert',
        ]}
        imageAccept="image/*"
      />
      {enableAudioPhrases && (
        <MarkdownAudioPhraseDialog
          open={audioDialogOpen}
          onOpenChange={setAudioDialogOpen}
          selectedText={selectedText}
          hasSelection={Boolean(selectedText.trim())}
          onInsert={handleAudioInsert}
        />
      )}
    </div>
  )
}

export default React.memo(MarkdownEditor)
